import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronRight,
  FaFileAlt,
  FaFolder,
  FaFolderOpen,
  FaLayerGroup,
  FaSearch,
} from "react-icons/fa";
import PageHeader from "src/components/pageHeader";
import PageLoading from "src/components/pageLoading";
import { useGetAICoachPromptsQuery } from "../ai-coach-prompts.service";
import { ImportPromptsYamlModal } from "../components/import-prompts-yaml-modal";
import { RequestPromptReplacementModal } from "../components/request-prompt-replacement-modal";
import { AICoachPrompt, AICoachPromptType } from "../entities/ai-coach-prompt.entity";

type PromptRow = AICoachPrompt & { id: string };
type PromptExplorerNode = {
  id: string;
  label: string;
  path: string;
  prompt?: PromptRow;
  children: PromptExplorerNode[];
};

const promptTypeUi: Record<
  AICoachPromptType,
  {
    label: string;
    badgeClass: string;
    accentClass: string;
  }
> = {
  SYSTEM: {
    label: "System Prompt",
    badgeClass: "border-cyan-200 bg-cyan-50 text-cyan-800",
    accentClass: "from-cyan-500/15 via-sky-500/10 to-transparent",
  },
  TOPIC: {
    label: "Topic Prompt",
    badgeClass: "border-emerald-200 bg-emerald-50 text-emerald-800",
    accentClass: "from-emerald-500/15 via-teal-500/10 to-transparent",
  },
  CUSTOM_SCENARIO: {
    label: "Custom Scenario",
    badgeClass: "border-amber-200 bg-amber-50 text-amber-800",
    accentClass: "from-amber-500/15 via-orange-500/10 to-transparent",
  },
};

const splitPromptKey = (promptKey: string) => promptKey.split(".").filter(Boolean);

const getPromptNamespace = (promptKey: string) => {
  const segments = splitPromptKey(promptKey);
  return segments.length > 1 ? segments[0] : "ROOT";
};

const sortPrompts = (prompts: PromptRow[]) =>
  [...prompts].sort((left, right) => {
    return left.promptKey.localeCompare(right.promptKey);
  });

const buildPromptExplorer = (prompts: PromptRow[]): PromptExplorerNode[] => {
  type TreeNode = PromptExplorerNode & {
    childMap: Map<string, TreeNode>;
  };

  const root = new Map<string, TreeNode>();

  prompts.forEach((prompt) => {
    const segments = splitPromptKey(prompt.promptKey);
    if (!segments.length) return;

    let currentLevel = root;
    let currentPath = "";

    segments.forEach((segment, index) => {
      currentPath = currentPath ? `${currentPath}.${segment}` : segment;
      const isLeaf = index === segments.length - 1;
      let node = currentLevel.get(segment);

      if (!node) {
        node = {
          id: currentPath,
          label: segment,
          path: currentPath,
          children: [],
          childMap: new Map<string, TreeNode>(),
        };
        currentLevel.set(segment, node);
      }

      if (isLeaf) node.prompt = prompt;
      currentLevel = node.childMap;
    });
  });

  const toExplorerNodes = (tree: Map<string, TreeNode>): PromptExplorerNode[] =>
    Array.from(tree.values())
      .map((node) => ({
        id: node.id,
        label: node.label,
        path: node.path,
        prompt: node.prompt,
        children: toExplorerNodes(node.childMap),
      }))
      .sort((left, right) => {
        const leftIsFolder = left.children.length > 0 || !left.prompt;
        const rightIsFolder = right.children.length > 0 || !right.prompt;

        if (leftIsFolder !== rightIsFolder) {
          return leftIsFolder ? -1 : 1;
        }

        return left.label.localeCompare(right.label);
      });

  return toExplorerNodes(root);
};

const PromptExplorerItem = ({
  node,
  depth,
  expandedPaths,
  selectedPrompt,
  onToggle,
  onSelect,
}: {
  node: PromptExplorerNode;
  depth: number;
  expandedPaths: Set<string>;
  selectedPrompt: PromptRow | null;
  onToggle: (path: string) => void;
  onSelect: (prompt: PromptRow) => void;
}) => {
  const isFolder = node.children.length > 0;
  const isExpanded = expandedPaths.has(node.path);
  const isSelected = Boolean(node.prompt && selectedPrompt?.id === node.prompt.id);

  if (isFolder) {
    return (
      <div>
        <div
          className={`group flex items-center gap-1 rounded-xl pr-2 transition-colors ${
            isSelected ? "bg-slate-950/5" : "hover:bg-slate-100"
          }`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
        >
          <button
            type="button"
            onClick={() => onToggle(node.path)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white hover:text-slate-700"
            aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
          >
            {isExpanded ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
          </button>
          <button
            type="button"
            onClick={() => node.prompt && onSelect(node.prompt)}
            className={`flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium transition-colors ${
              isSelected
                ? "text-slate-950"
                : "text-slate-700"
            }`}
          >
            <span className="text-amber-600">{isExpanded ? <FaFolderOpen /> : <FaFolder />}</span>
            <span className="truncate font-mono text-[12px]">{node.label}</span>
          </button>
        </div>

        {isExpanded && (
          <div className="space-y-1">
            {node.children.map((child) => (
              <PromptExplorerItem
                key={child.path}
                node={child}
                depth={depth + 1}
                expandedPaths={expandedPaths}
                selectedPrompt={selectedPrompt}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => node.prompt && onSelect(node.prompt)}
      className={`flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm transition-colors ${
        isSelected
          ? "bg-slate-950 text-white"
          : "text-slate-700 hover:bg-slate-100"
      }`}
      style={{ paddingLeft: `${depth * 16 + 28}px` }}
    >
      <span className={`${isSelected ? "text-slate-300" : "text-slate-400"}`}><FaFileAlt className="text-xs" /></span>
      <span className="truncate font-mono text-[12px]">{node.label}</span>
    </button>
  );
};

const AICoachPromptsPage = () => {
  const [query, setQuery] = useState("");
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [requestTarget, setRequestTarget] = useState<PromptRow | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>({});

  const { data: prompts = [], isLoading, isError } = useGetAICoachPromptsQuery();

  const sortedPrompts = useMemo(() => sortPrompts(prompts), [prompts]);

  const filteredPrompts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return sortedPrompts.filter((prompt) => {
      if (!normalizedQuery) return true;

      const haystack = [
        prompt.promptKey,
        prompt.title,
        prompt.currentPrompt,
        prompt.requestedPrompt,
        prompt.requestNote,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [query, sortedPrompts]);

  useEffect(() => {
    if (!filteredPrompts.length) {
      setSelectedPromptId(null);
      return;
    }

    const selectedStillVisible = filteredPrompts.some((prompt) => prompt.id === selectedPromptId);
    if (!selectedPromptId || !selectedStillVisible) {
      setSelectedPromptId(filteredPrompts[0].id);
    }
  }, [filteredPrompts, selectedPromptId]);

  const selectedPrompt = useMemo(
    () => filteredPrompts.find((prompt) => prompt.id === selectedPromptId) || null,
    [filteredPrompts, selectedPromptId]
  );

  const explorerTree = useMemo(() => buildPromptExplorer(filteredPrompts), [filteredPrompts]);
  const expandedPathSet = useMemo(() => new Set(Object.entries(expandedPaths).filter(([, isOpen]) => isOpen).map(([path]) => path)), [expandedPaths]);

  useEffect(() => {
    if (!explorerTree.length) return;

    setExpandedPaths((current) => {
      const next = { ...current };
      const shouldExpandAll = Boolean(query.trim());

      const visit = (node: PromptExplorerNode) => {
        const depth = splitPromptKey(node.path).length;

        if (node.children.length > 0) {
          if (shouldExpandAll) {
            next[node.path] = true;
          } else if (next[node.path] === undefined) {
            next[node.path] = depth === 1;
          }
        }

        node.children.forEach(visit);
      };

      explorerTree.forEach(visit);
      return next;
    });
  }, [explorerTree, query]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (isError) {
    return (
      <div className="page_container space-y-6">
        <PageHeader text="AI Coach Prompts" />
        <div className="rounded-[28px] border border-red-200 bg-red-50 p-8 text-center">
          <h2 className="text-xl font-semibold text-red-800">Unable to load AI coach prompts</h2>
          <p className="mt-2 text-sm text-red-700">
            The dashboard could not fetch the prompt registry from Firestore. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page_container space-y-8 pb-8">
      <PageHeader text="AI Coach Prompts" />

      {sortedPrompts.length === 0 ? (
        <section className="rounded-[28px] border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
            <FaCheckCircle className="text-xl" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-slate-900">No AI coach prompts found</h2>
          <p className="mt-2 text-sm text-slate-600">
            Add live prompt documents to the <span className="font-semibold">ai_coach_prompts</span> collection to populate this dashboard.
          </p>
        </section>
      ) : (
        <section className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Prompt registry</p>
                  <h2 className="mt-1 text-lg font-semibold text-slate-950">Explorer</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setIsImportModalOpen(true)}
                  className="rounded-full border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-800 transition-colors hover:border-slate-400 hover:bg-slate-100"
                >
                  Import YAML
                </button>
              </div>

              <div className="border-b border-slate-200 px-4 py-4">
                <div className="flex items-center gap-3 rounded-2xl bg-slate-100 px-4 py-3">
                  <FaSearch className="text-slate-500" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search by ID or content..."
                    className="w-full border-0 bg-transparent text-sm text-slate-900 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 placeholder:text-slate-400"
                  />
                </div>
              </div>

              <div className="max-h-[980px] overflow-y-auto p-4">
                {filteredPrompts.length ? (
                  <div className="space-y-1">
                    {explorerTree.map((node) => (
                      <PromptExplorerItem
                        key={node.path}
                        node={node}
                        depth={0}
                        expandedPaths={expandedPathSet}
                        selectedPrompt={selectedPrompt}
                        onToggle={(path) =>
                          setExpandedPaths((current) => ({
                            ...current,
                            [path]: !current[path],
                          }))
                        }
                        onSelect={(nextPrompt) => setSelectedPromptId(nextPrompt.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
                    <p className="text-base font-semibold text-slate-900">No prompts match this filter</p>
                    <p className="mt-2 text-sm text-slate-600">Try another filter or clear the search query.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-5">
            {selectedPrompt ? (
              <>
                <section className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm">
                  <div className={`border-b border-slate-200 bg-gradient-to-r ${promptTypeUi[selectedPrompt.promptType].accentClass} px-6 py-6`}>
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${promptTypeUi[selectedPrompt.promptType].badgeClass}`}>
                            {promptTypeUi[selectedPrompt.promptType].label}
                          </span>
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            selectedPrompt.requestStatus === "PENDING"
                              ? "bg-amber-100 text-amber-900"
                              : "bg-emerald-100 text-emerald-900"
                          }`}>
                            {selectedPrompt.requestStatus === "PENDING" ? "Pending review" : "No pending request"}
                          </span>
                        </div>
                        <h2 className="mt-4 break-all font-mono text-2xl font-semibold text-slate-950">
                          {selectedPrompt.promptKey}
                        </h2>
                        {splitPromptKey(selectedPrompt.promptKey).length > 1 ? (
                          <p className="mt-2 text-sm text-slate-600">
                            <span className="font-semibold text-slate-900">{splitPromptKey(selectedPrompt.promptKey)[0]}</span>
                            <span className="mx-2 text-slate-400">/</span>
                            <span className="font-mono">{splitPromptKey(selectedPrompt.promptKey).slice(1).join(".")}</span>
                          </p>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        onClick={() => setRequestTarget(selectedPrompt)}
                        className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-950/10 transition-all hover:bg-slate-800"
                      >
                        Request replacement
                      </button>
                    </div>
                  </div>
                </section>

                <section className="rounded-[32px] border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Live prompt</p>
                      <h3 className="mt-1 truncate font-mono text-sm font-semibold text-slate-950">
                        {selectedPrompt.promptKey}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${promptTypeUi[selectedPrompt.promptType].badgeClass}`}>
                        {promptTypeUi[selectedPrompt.promptType].label}
                      </span>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {getPromptNamespace(selectedPrompt.promptKey)}
                      </span>
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-slate-950 shadow-inner">
                      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                          <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                        </div>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Read only
                        </span>
                      </div>
                      <pre className="max-h-[860px] overflow-auto whitespace-pre-wrap px-5 py-5 font-mono text-[12px] leading-7 text-cyan-50">
                        {selectedPrompt.currentPrompt}
                      </pre>
                    </div>
                    {selectedPrompt.requestStatus === "PENDING" && selectedPrompt.requestedPrompt?.trim() ? (
                      <div className="mt-5 rounded-[24px] border border-amber-200 bg-amber-50 px-4 py-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-900">
                            Pending request
                          </span>
                          <span className="text-xs text-amber-900">
                            {selectedPrompt.requestedByAdminName || "Admin"} has already submitted a replacement.
                          </span>
                        </div>
                        <pre className="mt-3 max-h-[260px] overflow-auto whitespace-pre-wrap rounded-2xl border border-amber-200 bg-white px-4 py-4 font-mono text-[12px] leading-6 text-slate-800">
                          {selectedPrompt.requestedPrompt}
                        </pre>
                      </div>
                    ) : null}
                  </div>
                </section>
              </>
            ) : (
              <section className="rounded-[32px] border border-dashed border-slate-300 bg-slate-50 p-12 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-white text-slate-500 shadow-sm">
                  <FaLayerGroup className="text-2xl" />
                </div>
                <h2 className="mt-5 text-2xl font-semibold text-slate-950">Choose a prompt to inspect</h2>
                <p className="mt-2 text-sm leading-7 text-slate-600">
                  Pick a prompt from the registry on the left to read the live content, inspect pending request details, and submit a replacement request.
                </p>
              </section>
            )}
          </div>
        </section>
      )}

      <RequestPromptReplacementModal
        isOpen={Boolean(requestTarget)}
        prompt={requestTarget}
        onClose={() => setRequestTarget(null)}
      />
      <ImportPromptsYamlModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />
    </div>
  );
};

export default AICoachPromptsPage;
